import { useEffect, useRef, useState, useCallback } from 'react';
import '../css/stickman.css';

const STAGES = [
  { year: '2014', title: 'Founded', desc: 'Born in Immersion' },
  { year: '2016', title: 'Peak AR/VR', desc: 'Building Worlds' },
  { year: '2016-2026', title: 'The Silence', desc: 'Waiting, Watching' },
  { year: '2026', title: 'The Return', desc: 'Reborn in Intelligence' },
];

const DOT_POSITIONS = [0.125, 0.375, 0.625, 0.875];
const DOT_R = 6;
const YEAR_GAP = 20;

/* Fixed SVG coordinate space — scales via viewBox on all viewports */
const SVG_WIDTH = 1200;
const SVG_HEIGHT = 400;
const GROUND_Y = 300;

/* Anatomy — 50% larger than original */
const HEAD_R = 21;
const TORSO = 63;
const LIMB = 42;
const STAND_HIP_H = 72;
const STROKE = 2;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}

function easeOut(t) {
  return 1 - (1 - t) ** 2;
}

function easeIn(t) {
  return t * t;
}

/** Limb angle in radians: 0 = straight down, + = forward (+x) */
function limbEnd(ox, oy, angleRad, length = LIMB) {
  return {
    x: ox + Math.sin(angleRad) * length,
    y: oy + Math.cos(angleRad) * length,
  };
}

function getWalkAngles(phase, speed = 1, armLift = 0.55) {
  const swing = Math.sin(phase * Math.PI * 2 * speed);
  const legAmp = 0.42;
  const armAmp = 0.38 * armLift;

  return {
    legL: swing * legAmp,
    legR: -swing * legAmp,
    armL: -swing * armAmp,
    armR: swing * armAmp,
  };
}

function getJumpLift(t) {
  if (t <= 0) return 0;
  if (t < 0.12) return lerp(0, 14, easeIn(t / 0.12));
  if (t < 0.28) return lerp(14, -58, easeOut((t - 0.12) / 0.16));
  if (t < 0.52) return -58;
  if (t < 0.72) return lerp(-58, -58, (t - 0.52) / 0.2);
  if (t < 1) return lerp(-58, 0, easeInOut((t - 0.72) / 0.28));
  return 0;
}

function getPose(progress) {
  const p = clamp(progress, 0, 1);
  const stage = Math.min(3, Math.floor(p * 4));
  const local = p * 4 - stage;
  const walkPhase = p * 40;

  const xPercent = lerp(DOT_POSITIONS[0], DOT_POSITIONS[3], easeInOut(p));

  let hipY = -STAND_HIP_H;
  let headTilt = 0;
  let legL = 0.08;
  let legR = -0.08;
  let armL = -0.25;
  let armR = 0.25;
  let rootLift = 0;
  let stroke = '#FFFFFF';
  let glowing = false;

  if (stage === 0) {
    const w = getWalkAngles(walkPhase, 1, 1);
    legL = 0.1 + w.legL;
    legR = -0.1 + w.legR;
    armL = -0.2 + w.armL;
    armR = 0.2 + w.armR;
  } else if (stage === 1) {
    const w = getWalkAngles(walkPhase, 2.6, 0.2);
    legL = 0.12 + w.legL;
    legR = -0.12 + w.legR;
    armL = -2.05 + Math.sin(walkPhase * 8) * 0.08;
    armR = -2.05 + Math.cos(walkPhase * 8) * 0.08;
  } else if (stage === 2) {
    const sitT = easeInOut(local);
    hipY = lerp(-STAND_HIP_H, -STAND_HIP_H * 0.52, sitT);
    headTilt = sitT * 0.38;
    legL = lerp(0.1, 1.05, sitT);
    legR = lerp(-0.1, -0.65, sitT);
    armL = lerp(-0.2, 0.85, sitT);
    armR = lerp(0.2, 0.55, sitT);
  } else {
    const riseT = clamp(local / 0.22, 0, 1);
    const jumpT = local > 0.22 ? (local - 0.22) / 0.78 : 0;

    hipY = lerp(-STAND_HIP_H * 0.52, -STAND_HIP_H, easeInOut(riseT));
    headTilt = lerp(0.38, 0, easeInOut(riseT));
    rootLift = getJumpLift(jumpT);

    if (jumpT > 0) {
      const tuck = jumpT < 0.28 ? easeOut(jumpT / 0.28) : 1 - easeIn((jumpT - 0.28) / 0.72);
      legL = lerp(0.1, 0.55, tuck);
      legR = lerp(-0.1, -0.45, tuck);
      armL = lerp(-0.2, -2.2, tuck);
      armR = lerp(0.2, -2.2, tuck);
    } else {
      const w = getWalkAngles(walkPhase, 1, 0.6);
      legL = lerp(1.05, 0.1 + w.legL, easeInOut(riseT));
      legR = lerp(-0.65, -0.1 + w.legR, easeInOut(riseT));
      armL = lerp(0.85, -0.2 + w.armL, easeInOut(riseT));
      armR = lerp(0.55, 0.2 + w.armR, easeInOut(riseT));
    }

    stroke = jumpT > 0.15 ? '#650175' : '#FFFFFF';
    glowing = jumpT > 0.28 && jumpT < 0.75;
  }

  return {
    xPercent,
    hipY,
    headTilt,
    legL,
    legR,
    armL,
    armR,
    rootLift,
    stroke,
    glowing,
    stage,
  };
}

function captionOpacity(index, progress) {
  const start = index / 4;
  const end = (index + 1) / 4;
  const mid = (start + end) / 2;
  const half = 0.125;
  if (progress < start) return 0;
  if (progress > end) return 0;
  if (progress <= mid) return (progress - start) / half;
  return 1 - (progress - mid) / half;
}

function TimelineTrack({ stickmanX }) {
  const dots = DOT_POSITIONS.map((p) => p * SVG_WIDTH);
  const x = clamp(stickmanX, 0, SVG_WIDTH);

  const purpleSegments = [];

  if (x > 0) {
    purpleSegments.push({
      key: 'seg-start',
      x1: 0,
      x2: Math.min(x, dots[0]),
    });
  }

  for (let i = 0; i < dots.length - 1; i += 1) {
    if (x <= dots[i]) break;
    purpleSegments.push({
      key: `seg-${i}`,
      x1: dots[i],
      x2: Math.min(x, dots[i + 1]),
    });
  }

  if (x > dots[dots.length - 1]) {
    purpleSegments.push({
      key: 'seg-end',
      x1: dots[dots.length - 1],
      x2: x,
    });
  }

  return (
    <g className="stickman-track" aria-hidden="true">
      <line
        className="stickman-ground stickman-ground--base"
        x1={0}
        y1={GROUND_Y}
        x2={SVG_WIDTH}
        y2={GROUND_Y}
        stroke="#1A0F1F"
        strokeWidth={2}
        strokeLinecap="butt"
      />
      {purpleSegments.map((seg) => (
        <line
          key={seg.key}
          className="stickman-ground stickman-ground--active"
          x1={seg.x1}
          y1={GROUND_Y}
          x2={seg.x2}
          y2={GROUND_Y}
          stroke="#650175"
          strokeWidth={2}
          strokeLinecap="butt"
        />
      ))}
    </g>
  );
}

function TimelineDots({ stickmanX }) {
  const x = stickmanX;
  return (
    <g className="stickman-dots" aria-hidden="true">
      {DOT_POSITIONS.map((pos, i) => {
        const passed = x >= pos * SVG_WIDTH - 0.5;
        return (
          <circle
            key={STAGES[i].year}
            className={`stickman-dot${passed ? ' stickman-dot--active' : ''}`}
            cx={pos * SVG_WIDTH}
            cy={GROUND_Y}
            r={DOT_R}
            fill={passed ? '#650175' : '#1A0F1F'}
          />
        );
      })}
    </g>
  );
}

function StickmanFigure({ pose }) {
  const x = pose.xPercent * SVG_WIDTH;
  const footY = GROUND_Y + pose.rootLift;

  const hipY = footY + pose.hipY;
  const shoulderY = hipY - TORSO;
  const headCy = shoulderY - HEAD_R;

  const shoulderX = 0;
  const shoulderPt = { x: shoulderX, y: shoulderY };
  const hipPt = { x: 0, y: hipY };

  const drawLimb = (angleRad, ox, oy, key) => {
    const end = limbEnd(ox, oy, angleRad);
    return (
      <line
        key={key}
        x1={ox}
        y1={oy}
        x2={end.x}
        y2={end.y}
        stroke={pose.stroke}
        strokeWidth={STROKE}
        strokeLinecap="round"
      />
    );
  };

  return (
    <g
      className="stickman-figure"
      transform={`translate(${x}, 0)`}
      filter={pose.glowing ? 'url(#stickman-purple-glow)' : undefined}
    >
      {/* Head — bottom of circle meets top of torso */}
      <g transform={`rotate(${(pose.headTilt * 180) / Math.PI}, ${shoulderPt.x}, ${shoulderY})`}>
        <circle
          cx={shoulderPt.x}
          cy={headCy}
          r={HEAD_R}
          fill="none"
          stroke={pose.stroke}
          strokeWidth={STROKE}
        />
      </g>

      {/* Torso — hip to shoulders */}
      <line
        x1={hipPt.x}
        y1={hipPt.y}
        x2={shoulderPt.x}
        y2={shoulderPt.y}
        stroke={pose.stroke}
        strokeWidth={STROKE}
        strokeLinecap="round"
      />

      {/* Arms — single shoulder junction */}
      {drawLimb(pose.armL, shoulderPt.x, shoulderPt.y, 'armL')}
      {drawLimb(pose.armR, shoulderPt.x, shoulderPt.y, 'armR')}

      {/* Legs — single hip junction */}
      {drawLimb(pose.legL, hipPt.x, hipPt.y, 'legL')}
      {drawLimb(pose.legR, hipPt.x, hipPt.y, 'legR')}
    </g>
  );
}

function StickmanTimeline() {
  const scrollRef = useRef(null);
  const [progress, setProgress] = useState(0);

  const updateProgress = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const scrollable = rect.height - window.innerHeight;
    if (scrollable <= 0) {
      setProgress(0);
      return;
    }

    setProgress(clamp(-rect.top / scrollable, 0, 1));
  }, []);

  useEffect(() => {
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateProgress();
        ticking = false;
      });
    };

    const onResize = () => {
      updateProgress();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    onResize();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [updateProgress]);

  const pose = getPose(progress);
  const activeStage = pose.stage;
  const stickmanX = pose.xPercent * SVG_WIDTH;
  const yearLabelY = GROUND_Y + DOT_R + YEAR_GAP;

  return (
    <section
      ref={scrollRef}
      className="stickman-scroll"
      aria-label="MMI evolution timeline animation"
    >
      <div className="stickman-sticky">
        <div className="stickman-scene">
          <div className="stickman-grid-bg" aria-hidden="true" />

          <svg
            className="stickman-svg"
            viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label={`Timeline stage ${activeStage + 1} of 4: ${STAGES[activeStage].title}`}
          >
              <defs>
                <filter id="stickman-purple-glow" x="-80%" y="-80%" width="260%" height="260%">
                  <feGaussianBlur stdDeviation="8" result="blur" />
                  <feColorMatrix
                    in="blur"
                    type="matrix"
                    values="0 0 0 0 0.396
                            0 0 0 0 0.004
                            0 0 0 0 0.459
                            0 0 0 0.9 0"
                    result="purpleBlur"
                  />
                  <feMerge>
                    <feMergeNode in="purpleBlur" />
                    <feMergeNode in="purpleBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <pattern id="stickman-dot-grid" width="24" height="24" patternUnits="userSpaceOnUse">
                  <circle cx="1" cy="1" r="0.6" fill="rgba(101, 1, 117, 0.12)" />
                </pattern>
              </defs>

              <rect width="100%" height="100%" fill="url(#stickman-dot-grid)" opacity="0.35" />

            <TimelineTrack stickmanX={stickmanX} />

            <StickmanFigure pose={pose} />

            <TimelineDots stickmanX={stickmanX} />

            {STAGES.map((s, i) => {
              const passed = stickmanX >= DOT_POSITIONS[i] * SVG_WIDTH - 0.5;
              return (
                <text
                  key={`year-${s.year}`}
                  className={`stickman-year${passed ? ' stickman-year--active' : ''}`}
                  x={DOT_POSITIONS[i] * SVG_WIDTH}
                  y={yearLabelY}
                    textAnchor="middle"
                    dominantBaseline="hanging"
                    fill={passed ? '#FFFFFF' : '#888899'}
                  >
                    {s.year}
                  </text>
                );
              })}
            </svg>

            <div className="stickman-captions">
              {STAGES.map((s, i) => {
                const opacity = captionOpacity(i, progress);
                if (opacity <= 0.01) return null;
                return (
                  <p
                    key={`caption-${s.year}`}
                    className={`stickman-caption${opacity > 0.5 ? ' stickman-caption--current' : ''}`}
                    style={{ opacity }}
                  >
                    <span className="stickman-caption-year">{s.year}</span>
                    {' — '}
                    <span className="stickman-caption-title">{s.title}</span>
                    {' — '}
                    <span className="stickman-caption-desc">{s.desc}</span>
                  </p>
                );
              })}
            </div>

            <div className="stickman-progress-hint" aria-hidden="true">
              <span style={{ transform: `scaleX(${progress})` }} />
            </div>
          </div>
        </div>
      </section>
  );
}

export default StickmanTimeline;
